import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Plus, Search, Calendar, Clock, AlertCircle, CheckCircle2,
  User, Building2, Target, ChevronRight, Filter
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-green-100 text-green-700 border-green-200',
};

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-purple-100 text-purple-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

const CRMTaskBoard = ({ companies, contacts, opportunities, onCreateTask }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (showOverdueOnly) params.overdue_only = true;
      
      const response = await api.crm.getTasks(params);
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, showOverdueOnly]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api.crm.updateTask(taskId, { status: newStatus });
      toast.success('Task updated');
      loadTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleCompleteTask = async (taskId) => {
    await handleStatusChange(taskId, 'completed');
  };

  const filteredTasks = tasks.filter(task => {
    if (!searchTerm) return true;
    return (
      task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.contact_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const groupedTasks = {
    overdue: filteredTasks.filter(t => t.is_overdue && t.status !== 'completed'),
    today: filteredTasks.filter(t => {
      if (t.is_overdue || t.status === 'completed') return false;
      if (!t.due_date) return false;
      const today = new Date().toDateString();
      const dueDate = new Date(t.due_date).toDateString();
      return today === dueDate;
    }),
    upcoming: filteredTasks.filter(t => {
      if (t.is_overdue || t.status === 'completed') return false;
      if (!t.due_date) return false;
      const today = new Date();
      const dueDate = new Date(t.due_date);
      return dueDate > today && new Date(t.due_date).toDateString() !== today.toDateString();
    }),
    completed: filteredTasks.filter(t => t.status === 'completed'),
    noDueDate: filteredTasks.filter(t => !t.due_date && t.status !== 'completed'),
  };

  const TaskCard = ({ task }) => (
    <Card 
      className={`mb-2 hover:shadow-md transition-shadow ${task.is_overdue ? 'border-l-4 border-l-red-500' : ''}`}
      data-testid={`task-card-${task.id}`}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={task.status === 'completed'}
            onCheckedChange={() => handleCompleteTask(task.id)}
            className="mt-1"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h4 className={`font-medium text-sm ${task.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                {task.title}
              </h4>
              <Badge variant="outline" className={PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium}>
                {task.priority}
              </Badge>
            </div>
            
            {task.description && (
              <p className="text-xs text-slate-500 mb-2 line-clamp-2">{task.description}</p>
            )}
            
            <div className="flex items-center gap-3 text-xs text-slate-500">
              {task.due_date && (
                <span className={`flex items-center gap-1 ${task.is_overdue ? 'text-red-600 font-medium' : ''}`}>
                  <Calendar className="h-3 w-3" />
                  {new Date(task.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </span>
              )}
              {task.company_name && (
                <span className="flex items-center gap-1">
                  <Building2 className="h-3 w-3" />
                  {task.company_name}
                </span>
              )}
              {task.contact_name && (
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {task.contact_name}
                </span>
              )}
              {task.opportunity_name && (
                <span className="flex items-center gap-1">
                  <Target className="h-3 w-3" />
                  {task.opportunity_name}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const TaskSection = ({ title, tasks, icon: Icon, color, emptyMessage }) => (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-4 w-4 ${color}`} />
        <h3 className="font-semibold text-sm text-slate-700">{title}</h3>
        <Badge variant="secondary" className="text-xs">{tasks.length}</Badge>
      </div>
      {tasks.length > 0 ? (
        tasks.map(task => <TaskCard key={task.id} task={task} />)
      ) : (
        <p className="text-sm text-slate-400 italic">{emptyMessage}</p>
      )}
    </div>
  );

  if (loading) {
    return <div className="p-6 text-center text-slate-500">Loading tasks...</div>;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Task Board</h2>
          <p className="text-sm text-slate-500">Manage your CRM tasks and follow-ups</p>
        </div>
        <Button onClick={onCreateTask} data-testid="create-task-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Task
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Checkbox
            id="overdue"
            checked={showOverdueOnly}
            onCheckedChange={setShowOverdueOnly}
          />
          <label htmlFor="overdue" className="text-sm text-slate-600 cursor-pointer">
            Overdue only
          </label>
        </div>
      </div>

      {/* Task Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <TaskSection
            title="Overdue"
            tasks={groupedTasks.overdue}
            icon={AlertCircle}
            color="text-red-500"
            emptyMessage="No overdue tasks"
          />
          <TaskSection
            title="Due Today"
            tasks={groupedTasks.today}
            icon={Clock}
            color="text-amber-500"
            emptyMessage="No tasks due today"
          />
          <TaskSection
            title="Upcoming"
            tasks={groupedTasks.upcoming}
            icon={Calendar}
            color="text-blue-500"
            emptyMessage="No upcoming tasks"
          />
        </div>
        <div>
          <TaskSection
            title="No Due Date"
            tasks={groupedTasks.noDueDate}
            icon={Filter}
            color="text-slate-400"
            emptyMessage="No tasks without due date"
          />
          <TaskSection
            title="Completed"
            tasks={groupedTasks.completed}
            icon={CheckCircle2}
            color="text-green-500"
            emptyMessage="No completed tasks"
          />
        </div>
      </div>
    </div>
  );
};

export default CRMTaskBoard;
